import { useMemo } from 'react'
import { useStableLiveQuery } from '../../../../../lib/db/useStableLiveQuery'
import { useActivePausedLiveQuery } from '../../../../../lib/db/useActivePausedLiveQuery'
import { useStableEntityArray, baseEntityIdentity } from '../../../../../lib/db/useStableEntityArray'
import { db } from '../../../../../lib/db/indexeddb'
import type { Task } from '../../../../../lib/db/models'

interface UseDormantTasksArgs {
  lifeSpace: 'personal' | 'work' | 'home'
  isActive: boolean
}

/**
 * Live-queries tasks with `pausedAt != null` scoped to the given life space.
 * Orphan tasks (no `projectId`) surface in their own lifespace via
 * `task.lifeSpace`, mirroring `filterUpcomingTasks` — paused Kurz-notiert
 * entries from Work end up in Work's Ruhend, not Personal's.
 * Sorted by `pausedAt` desc so the most recently paused task is on top.
 */
export function useDormantTasks({ lifeSpace, isActive }: UseDormantTasksArgs): Task[] | undefined {
  const projects = useStableLiveQuery(
    () => db.projects
      .where('lifeSpace')
      .equals(lifeSpace)
      .filter(p => !p.deletedAt)
      .toArray(),
    [lifeSpace],
  )

  const lifeSpaceProjectIds = useMemo(
    () => new Set((projects ?? []).map(p => p.id)),
    [projects],
  )

  // `pausedAt` is not indexed — iterate + filter. Tasks table is small
  // enough that full scans inside a useLiveQuery are fine; the subscription
  // pauses while the tab is inactive via useActivePausedLiveQuery.
  const rawTasks = useActivePausedLiveQuery(
    () => db.tasks
      .filter(t => !t.deletedAt && t.pausedAt != null && t.status !== 'completed')
      .toArray(),
    [],
    isActive,
  )

  const tasks = useStableEntityArray(rawTasks, baseEntityIdentity)

  return useMemo(() => {
    if (!tasks || !projects) return undefined
    const filtered = tasks.filter(task => {
      // Orphan tasks (no project) surface only in their own lifespace,
      // read from `task.lifeSpace`. Pre-migration code routed all orphans
      // to Personal — that bug landed Work-Clarify pauses in Personal's
      // Ruhend.
      if (task.projectId == null) {
        if (task.lifeSpace !== lifeSpace) return false
      } else if (!lifeSpaceProjectIds.has(task.projectId)) {
        return false
      }
      return true
    })
    // Most-recently paused first. `filtered` is already a fresh array from
    // `.filter()`, so we can sort in place without a defensive copy.
    return filtered.sort((a, b) => {
      const aMs = a.pausedAt ? a.pausedAt.getTime() : 0
      const bMs = b.pausedAt ? b.pausedAt.getTime() : 0
      return bMs - aMs
    })
  }, [tasks, projects, lifeSpace, lifeSpaceProjectIds])
}
